import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";

import { RootState } from "../../redux/store";
import { TopupPurchaseRequest } from "./types/topup-purchase-types";
import {
  DataPurchaseState,
  PurchaseResponse,
  SourceType,
  PurchaseSuccessPayload,
  Transaction,
} from "./types/data-purchase-types";

const API_URL = process.env.REACT_APP_API_URL;
const PURCHASE_ENDPOINT = `${API_URL}/top-ups-purchase`;

export const purchaseTopup = createAsyncThunk<
  PurchaseResponse,
  TopupPurchaseRequest,
  { rejectValue: { message: string }; state: RootState }
>("topupPurchase/purchaseTopup", async (payload, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const normalized = {
      ...payload,
      source: payload.source?.toUpperCase() as SourceType | undefined,
    };

    const res = await axios.post(PURCHASE_ENDPOINT, normalized, {
      headers: { Authorization: `Bearer ${token}` },
    });

    return res.data as PurchaseResponse;
  } catch (err: any) {
    const message =
      err?.response?.data?.message || err?.message || "Purchase failed";
    return thunkAPI.rejectWithValue({ message });
  }
});

const initialState: DataPurchaseState = {
  loading: false,
  error: null,
  message: null,
  transaction: null,
  paymentUrl: null,
  lastStatus: "idle",
};

const topupPurchaseSlice = createSlice({
  name: "topupPurchase",
  initialState,
  reducers: {
    resetPurchaseState: () => initialState,
    setPaymentUrl: (state, action: PayloadAction<string | null>) => {
      state.paymentUrl = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(purchaseTopup.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.message = null;
        state.paymentUrl = null;
        state.transaction = null;
        state.lastStatus = "initiated";
      })
      .addCase(purchaseTopup.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload.message;

        if (action.payload.status === "error") {
          state.error = action.payload.message || "Purchase error";
          state.lastStatus = "error";
          return;
        }

        state.error = null;
        const data = (action.payload as PurchaseSuccessPayload).data;

        if (
          data &&
          typeof data === "object" &&
          "payment_url" in data &&
          (data as any).payment_url
        ) {
          state.paymentUrl = (data as any).payment_url as string;
          state.lastStatus = "initiated";
          return;
        }

        if (data && typeof data === "object") {
          state.transaction = data as Transaction;

          const s = (data as any).status as string | undefined;
          if (s === "SUCCESSFUL") {
            state.lastStatus = "successful";
          } else if (
            s === "AWAITINGPAYMENT" ||
            s === "PROCESSING" ||
            s === "QUEUED"
          ) {
            state.lastStatus = "processing";
          } else if (s === "FAILED" || s === "CANCELLED") {
            state.lastStatus = "error";
          } else {
            state.lastStatus = "processing";
          }
        }
      })
      .addCase(purchaseTopup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Purchase failed";
        state.lastStatus = "error";
      });
  },
});

export const { resetPurchaseState, setPaymentUrl } =
  topupPurchaseSlice.actions;
export default topupPurchaseSlice.reducer;

export const selectPurchaseLoading = (state: RootState) =>
  state.purchaseTopup.loading;
export const selectPurchaseError = (state: RootState) =>
  state.purchaseTopup.error;
export const selectPurchaseMessage = (state: RootState) =>
  state.purchaseTopup.message;
export const selectPurchasePaymentUrl = (state: RootState) =>
  state.purchaseTopup.paymentUrl;
export const selectPurchaseTransaction = (state: RootState) =>
  state.purchaseTopup.transaction;
export const selectPurchaseStatus = (state: RootState) =>
  state.purchaseTopup.lastStatus;
